import { z } from "zod";
import { decimalByteCountSchema } from "./seedance.js";
import { canvasEdgeSchema, canvasGroupSchema, canvasNodeSchema, maxCanvasDocumentBytes, pointSchema, seedanceNodeSettingsSchema, workflowInsertionViewSchema } from "./studio.js";
import { promptBindingSchema, promptContentSchema, promptToPlainText, type PromptContent, type PromptSegment, type PromptSource } from "./prompt-content.js";

const boundedPrompt = (maximum: number) => promptContentSchema.refine(
  (prompt) => promptToPlainText(prompt).length <= maximum,
  `文字与引用标签合计不得超过 ${maximum} 个字符。`
);
const baseNode = z.strictObject({
  id: z.uuid(), position: pointSchema,
  size: z.strictObject({ width: z.number().finite().min(120).max(2400), height: z.number().finite().min(80).max(2400) }),
  collapsed: z.boolean()
});
export const seedanceNodeSettingsV3Schema = seedanceNodeSettingsSchema.extend({ prompt: boundedPrompt(20_000) });
export const canvasNodeV3Schema = z.discriminatedUnion("kind", [
  baseNode.extend({ kind: z.literal("text"), text: boundedPrompt(50_000) }),
  // Reuse unchanged V2 media schemas, without broadening the old public union.
  canvasNodeSchema.options[1], canvasNodeSchema.options[2], canvasNodeSchema.options[3],
  baseNode.extend({ kind: z.literal("script"), title: boundedPrompt(160), synopsis: boundedPrompt(20_000) }),
  baseNode.extend({ kind: z.literal("seedance"), settings: seedanceNodeSettingsV3Schema, referenceBindings: z.array(promptBindingSchema).max(10_000) })
]);
export type CanvasNodeV3 = z.infer<typeof canvasNodeV3Schema>;
export type SeedanceNodeSettingsV3 = z.infer<typeof seedanceNodeSettingsV3Schema>;

const documentShapeSchema = z.strictObject({
  schemaVersion: z.literal(3),
  viewport: pointSchema.extend({ zoom: z.number().finite().min(0.05).max(4) }),
  nodes: z.array(canvasNodeV3Schema).max(5_000),
  edges: z.array(canvasEdgeSchema).max(10_000),
  groups: z.array(canvasGroupSchema).max(1_000)
});
export type CanvasDocumentV3 = z.infer<typeof documentShapeSchema>;

function fieldContent(node: CanvasNodeV3 | undefined, field: PromptSource["field"]): PromptContent | undefined {
  if (node?.kind === "seedance" && field === "prompt") return node.settings.prompt;
  if (node?.kind === "text" && field === "text") return node.text;
  if (node?.kind === "script" && (field === "title" || field === "synopsis")) return node[field];
  return undefined;
}

export function getPromptContent(document: CanvasDocumentV3, source: PromptSource): PromptContent {
  const node = document.nodes.find((item) => item.id === source.nodeId);
  const prompt = fieldContent(node, source.field);
  if (!prompt) throw new Error("提示词来源或字段无效。");
  if (source.inputEdgeId !== null) {
    const edge = document.edges.find((item) => item.id === source.inputEdgeId);
    if (!edge || edge.sourceNodeId !== source.nodeId || !document.nodes.some((item) => item.id === edge.targetNodeId && item.kind === "seedance")) {
      throw new Error("提示词输入连线无效。");
    }
  }
  return prompt;
}

export const canvasDocumentV3Schema = documentShapeSchema.superRefine((document, context) => {
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const edges = new Map(document.edges.map((edge) => [edge.id, edge]));
  const slotsByPrompt = new Map<PromptContent, Map<string, Extract<PromptSegment, { type: "reference" }>>>();
  const getSlot = (prompt: PromptContent | undefined, slotId: string) => {
    if (!prompt) return undefined;
    let slots = slotsByPrompt.get(prompt);
    if (!slots) {
      slots = new Map();
      for (const segment of prompt.segments) if (segment.type === "reference") slots.set(segment.slotId, segment);
      slotsByPrompt.set(prompt, slots);
    }
    return slots.get(slotId);
  };
  for (const name of ["nodes", "edges", "groups"] as const) {
    const ids = new Set<string>();
    document[name].forEach((item, index) => {
      if (ids.has(item.id)) context.addIssue({ code: "custom", path: [name, index, "id"], message: "画布集合内的 ID 必须唯一。" });
      ids.add(item.id);
    });
  }
  document.edges.forEach((edge, index) => {
    if (!nodes.has(edge.sourceNodeId) || !nodes.has(edge.targetNodeId)) {
      context.addIssue({ code: "custom", path: ["edges", index], message: "画布连线端点不存在。" });
    }
  });
  document.groups.forEach((group, index) => {
    if (new Set(group.nodeIds).size !== group.nodeIds.length || group.nodeIds.some((id) => !nodes.has(id))) {
      context.addIssue({ code: "custom", path: ["groups", index, "nodeIds"], message: "分组节点不存在或重复。" });
    }
  });
  document.nodes.forEach((node, nodeIndex) => {
    if (node.kind !== "seedance") return;
    const keys = new Set<string>();
    node.referenceBindings.forEach((binding, bindingIndex) => {
      const { source, slotId, value } = binding;
      const key = JSON.stringify([source.nodeId, source.field, source.inputEdgeId, slotId]);
      const sourceNode = nodes.get(source.nodeId);
      const input = source.inputEdgeId === null ? undefined : edges.get(source.inputEdgeId);
      const correctSource = source.inputEdgeId === null
        ? source.nodeId === node.id && source.field === "prompt"
        : input?.sourceNodeId === source.nodeId && input.targetNodeId === node.id && (sourceNode?.kind === "text" || sourceNode?.kind === "script");
      const segment = getSlot(fieldContent(sourceNode, source.field), slotId);
      let valid = !keys.has(key) && correctSource && segment?.type === "reference";
      keys.add(key);
      if (value.state === "bound") {
        const target = value.target;
        const edge = edges.get(target.edgeId);
        const media = nodes.get(target.nodeId);
        valid = valid && edge?.id === target.edgeId && edge.sourceNodeId === target.nodeId
          && edge.targetNodeId === node.id && media?.kind === target.kind
          && "assetId" in media && media.assetId === target.assetId
          && segment?.type === "reference" && segment.kind === target.kind;
      }
      if (!valid) context.addIssue({
        code: "custom", path: ["nodes", nodeIndex, "referenceBindings", bindingIndex],
        message: "引用绑定与提示词来源或画布素材身份不一致。",
        // Safe structured metadata lets the browser resolver show a local diagnostic
        // even for a stale, not-yet-normalized editing document. No asset metadata.
        params: { referenceBinding: true, targetNodeId: node.id, source, slotId }
      });
    });
  });
  if (new TextEncoder().encode(JSON.stringify(document)).byteLength > maxCanvasDocumentBytes) {
    context.addIssue({ code: "custom", message: "画布文档的未压缩 JSON 不得超过 10 MiB。" });
  }
});

export const canvasViewV3Schema = z.strictObject({
  canvasId: z.uuid(), projectId: z.uuid(), revision: decimalByteCountSchema,
  document: canvasDocumentV3Schema, updatedAt: z.iso.datetime()
});
export const saveCanvasV3RequestSchema = z.strictObject({ revision: decimalByteCountSchema, document: canvasDocumentV3Schema });
export type CanvasViewV3 = z.infer<typeof canvasViewV3Schema>;
export type SaveCanvasV3Request = z.infer<typeof saveCanvasV3RequestSchema>;
export const workflowInsertionViewV3Schema = workflowInsertionViewSchema.extend({ canvas: canvasViewV3Schema });
export type WorkflowInsertionViewV3 = z.infer<typeof workflowInsertionViewV3Schema>;
